/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useServerVersion, useVersionFooter } from '../../screens/system/hooks/useSystemInformation'
import { useServiceContext } from '../../root'
import { getAssetPath } from '../../utils/assetPath'

interface TenantElement {
  name: string
  id: string
}

interface TenantResponse {
  elements?: TenantElement[]
}

const GlobalFooter: React.FC = () => {
  const { t } = useTranslation()
  const { data: serverData } = useServerVersion()
  const { showInFooter } = useVersionFooter()
  const { api, service } = useServiceContext()

  const [tenantName, setTenantName] = useState<string>('')

  // Version information
  const clientBuild = document.documentElement.getAttribute('build-version') || '2026.03.13.00.00'
  const clientVersion = document.documentElement.getAttribute('version') || '2026.1.0'
  const currentYear = new Date().getFullYear()

  // Load tenant name
  useEffect(() => {
    const loadTenantData = async () => {
      try {
        const tenantUuid = service.auth.getTenantUuid()
        if (tenantUuid) {
          const result = (await api.settings.getTenantByUuid(tenantUuid)) as TenantResponse
          if (result?.elements?.[0]?.name) {
            setTenantName(result.elements[0].name)
          }
        }
      } catch (error) {
        console.error('Error loading tenant data:', error)
      }
    }

    loadTenantData()
  }, [api, service])

  return (
    <footer className="d-flex align-items-stretch mcc-sandman theme-sandman" id="footer">
      <div className="d-flex flex-grow" id="footer-wrapper">
        <div className="footer-logo-link">
          <img
            src={getAssetPath('medidata_logo_footer.svg')}
            alt="Medidata logo"
            title={tenantName}
            onError={(e) => {
              console.error('Logo failed to load:', e)
              e.currentTarget.style.display = 'none'
            }}
            onLoad={() => console.log('Logo loaded successfully')}
          />
        </div>

        <div className="footer-text">
          <span>
            {t('FOOTER_TEXT_COPYRIGHT')} &copy; 1999-{currentYear}
          </span>

          <span className="footer-item separator">
            <a href="http://mdsol.com/" target="_blank" rel="noopener noreferrer">
              {t('FOOTER_LINK_MEDIDATA_NAME')}
            </a>
          </span>

          <span className="footer-item separator">
            <a
              href="https://learn.mdsol.com/imedidata/en/medidata-privacy-policy-69851277.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('FOOTER_LINK_PRIVACY_POLICY')}
            </a>
          </span>

          <span className="footer-item separator">
            <a
              href="https://learn.mdsol.com/imedidata/en/imedidata-terms-of-use-69851276.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('FOOTER_LINK_TERMS_OF_USE')}
            </a>
          </span>

          {showInFooter && (
            <div className="versioninfo">
              <span>
                {t('FOOTER_VERSION_UI_VERSION')}: {clientVersion} | {t('FOOTER_VERSION_UI_BUILD')}: {clientBuild} GMT
                {serverData?.versionTag && (
                  <>
                    {' '}
                    | {t('FOOTER_VERSION_SERVER_VERSION_TAG')}: {serverData.versionTag}
                  </>
                )}
                {serverData?.version && (
                  <>
                    {' '}
                    | {t('FOOTER_VERSION_SERVER_VERSION')}: {serverData.version}
                  </>
                )}
                {serverData?.build && (
                  <>
                    {' '}
                    | {t('FOOTER_VERSION_SERVER_BUILD')}: {serverData.build} GMT
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </footer>
  )
}

export default GlobalFooter
